import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { useTableSearch } from '@ui/v2/Table/hooks/useTableSearch'
import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { conditionalReverse } from '@utils/array'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { prepareExportVisibleDataPayload } from '@utils/queryParser'

import { AccountSummaryDto } from '@model/AccountSummaryDto'
import { ExportHubReportSectionId } from '@model/ExportHub'
import { PlanValuesKeys } from '@model/MultiTierDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { IBReportResultsPage } from '../IBReportResultsPage'
import { useBrokerSummaryFetch } from '../hooks/useClientsOverallSummary'
import { IBClientsTable, IBClientsTableSortKeys } from './IBClientsTable'
import { ClientColumnCtx, clientColumns } from './IBClientsTable.columns'
import {
  IB_CLIENTS_REPORTS_SEARCH_TERM,
  useAccountSummaryFetch,
} from './hooks/useClientsPerAccountSummary'

const useClientsSearch = () =>
  useTableSearch<IBClientsTableSortKeys, AccountSummaryDto, ClientColumnCtx>({
    columns: clientColumns,
    placeholderKey: 'IB.Name or account number',
  })

const useClientsGenerateDocumentFile = (data: AccountSummaryDto[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { formatNumber } = useFormatNumber()
  const isArabic = useArabicSessionLanguage()

  const tableBody = () => {
    const headerCsvData = [
      conditionalReverse(
        [
          t('Trading Account.Trading Account'),
          t('IB.Client Name'),
          t('IB.Account Type'),
          t('IB.Registration Date'),
          t('IB.Country'),
          t('IB.Entity'),
          t('IB.Currency'),
          t('IB.ReportsPage.Clients.Vol.FXM'),
          t('IB.ReportsPage.Clients.Vol.Other'),
          t('IB.ReportsPage.Clients.Commission.FXM'),
          t('IB.ReportsPage.Clients.Commission.Other'),
          t('IB.Spread Revenue'),
        ],
        isArabic
      ),
    ]
    return data.reduce((previousValue, currentValue) => {
      const isAccountTypeClassic = PlanValuesKeys.Classic === currentValue.accountType

      return previousValue.concat([
        conditionalReverse(
          [
            currentValue?.login?.toString() ?? t('N/A'),
            currentValue.clientName,
            currentValue.accountType ?? t('N/A'),
            formatDate(currentValue.registrationDate, { formatType: dateFormat }),
            currentValue.country,
            currentValue.tickmillCompany?.name || t('N/A'),
            currentValue.currency ?? t('N/A'),
            formatNumber(currentValue.fxmVolume),
            formatNumber(currentValue.otherVolume),
            formatNumber(currentValue.fxmAmount),
            formatNumber(currentValue.otherAmount),
            isAccountTypeClassic ? formatNumber(currentValue.spreadRevenue ?? 0) : t('N/A'),
          ],
          isArabic
        ),
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('clients_report', tableBody())
}

const useFilterOptions = () => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: filterOptions } = useQuery({
    queryKey: queryKeys.ib.clientFilterOptions(ibId),
    queryFn: () => apiClient.getIBClientFilterOptions(ibId!),
    enabled: !!ibId,
  })

  return { filterOptions }
}

export const IBClientsPage: React.FC = () => {
  const { t } = useTranslation()

  const search = useClientsSearch()
  const brokerSummary = useBrokerSummaryFetch({ search })
  const ibAccountSummary = useAccountSummaryFetch({ search })
  const generateDocumentFile = useClientsGenerateDocumentFile(ibAccountSummary.query.data)
  const { filterOptions } = useFilterOptions()
  const exportSearch = prepareExportVisibleDataPayload(ibAccountSummary.query, search.search, {
    [IB_CLIENTS_REPORTS_SEARCH_TERM]: search.searchTerm,
  })

  const tradePeriodFrom = search.search['ca_search_DateFrom'] as string | undefined
  const tradePeriodTo = search.search['ca_search_DateTo'] as string | undefined
  return (
    <IBReportResultsPage
      title={t('IB.Clients')}
      data={ibAccountSummary}
      summary={
        <TableSummary
          isLoading={brokerSummary.isLoading}
          summaryItems={brokerSummary.summaryItems}
        />
      }
      search={search}
      generateDocumentFile={generateDocumentFile}
      exportData={{
        section: ExportHubReportSectionId.Clients,
        filters: {
          dateFrom: (search.search['ca_search_RegistrationDateFrom'] as string) || null,
          dateTo: (search.search['ca_search_RegistrationDateTo'] as string) || null,
          search: {
            ...exportSearch,
            ...(tradePeriodFrom && { TradePeriodFrom: tradePeriodFrom }),
            ...(tradePeriodTo && { TradePeriodTo: tradePeriodTo }),
          },
        },
      }}
      scrollToHeaderId={ScrollToIds.IBSignupHeader}
      filterOptions={filterOptions}
      filterOptionsTypes={[
        FilterOptions.HasTradingAccounts,
        FilterOptions.AccountType,
        FilterOptions.RegistrationDate,
        FilterOptions.Country,
        FilterOptions.Entity,
        FilterOptions.TradePeriod,
        FilterOptions.FxmVolume,
        FilterOptions.OtherVolume,
        FilterOptions.FxmCommission,
        FilterOptions.OtherCommission,
      ]}
      table={<IBClientsTable data={ibAccountSummary} search={search} />}
    />
  )
}
